import React, { useEffect } from 'react';
import { ReactLenis, useLenis } from 'lenis/react';
import { useReducedMotion } from 'framer-motion';

type Props = {
  children: React.ReactNode;
};

/**
 * Hands scrolling back to the browser while the user is selecting text.
 * Lenis's smooth scrolling otherwise fights native text selection (it
 * keeps lerping the scroll position back), which makes highlighting and
 * copying text feel broken. Pausing Lenis during a selection lets the
 * browser select and auto-scroll normally; it resumes on release.
 */
const SelectionScrollFix: React.FC = () => {
  const lenis = useLenis();

  useEffect(() => {
    if (!lenis) return;

    let paused = false;
    const pause = () => {
      if (paused) return;
      paused = true;
      lenis.stop();
    };
    const resume = () => {
      if (!paused) return;
      paused = false;
      lenis.start();
    };

    document.addEventListener('selectstart', pause);
    window.addEventListener('mouseup', resume);
    window.addEventListener('pointerup', resume);
    window.addEventListener('keyup', resume);
    window.addEventListener('blur', resume);

    return () => {
      document.removeEventListener('selectstart', pause);
      window.removeEventListener('mouseup', resume);
      window.removeEventListener('pointerup', resume);
      window.removeEventListener('keyup', resume);
      window.removeEventListener('blur', resume);
      resume();
    };
  }, [lenis]);

  return null;
};

const SmoothScroll: React.FC<Props> = ({ children }) => {
  const prefersReducedMotion = useReducedMotion();

  if (prefersReducedMotion) {
    return <>{children}</>;
  }

  return (
    <ReactLenis
      root
      options={{
        duration: 1.1,
        easing: (t: number) => 1 - Math.pow(1 - t, 3),
        smoothWheel: true,
        syncTouch: false,
      }}
    >
      <SelectionScrollFix />
      {children}
    </ReactLenis>
  );
};

export default SmoothScroll;
